import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { INTERNAL_ROLES, CLIENT_ROLES, AFFILIATE_ROLES, ROLES } from "@/lib/constants";
import { devWarn } from "@/lib/logger";
import OrgPicker from "@/components/OrgPicker";

export default function InviteEmployee() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isInternal = user?.org_type === "internal";
  const [mode, setMode] = useState("invite"); // invite | manual
  const [meta, setMeta] = useState({ roles: {}, permissions: [], defaults: {} });
  const [orgType, setOrgType] = useState(isInternal ? "internal" : user?.org_type);
  const [form, setForm] = useState({
    email: "", name: "", role: "", permissions: [],
    org_id: user?.org_id || "", temporary_password: "",
    scoped_client_ids: [], scoped_affiliate_ids: [], scoped_campaign_ids: [],
    send_email: true,
  });

  useEffect(() => {
    api.get("/employees/_meta/roles").then((r) => setMeta(r.data));
  }, []);

  // Roles available based on org type
  const roleOptions = useMemo(() => {
    if (!isInternal) {
      return user?.org_type === "client" ? CLIENT_ROLES : AFFILIATE_ROLES;
    }
    if (orgType === "internal") return INTERNAL_ROLES;
    if (orgType === "client") return CLIENT_ROLES;
    return AFFILIATE_ROLES;
  }, [isInternal, orgType, user]);

  // Update default perms when role changes
  useEffect(() => {
    if (form.role && meta.defaults?.[form.role]) {
      setForm((f) => ({ ...f, permissions: meta.defaults[form.role] }));
    }
  }, [form.role, meta.defaults]);

  const togglePerm = (p) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      let orgId = null;
      if (isInternal) {
        if (orgType !== "internal") orgId = form.org_id;
      } else {
        orgId = user.org_id;
      }
      const base = {
        email: form.email, name: form.name, role: form.role,
        permissions: form.permissions, org_id: orgId,
        scoped_client_ids: form.scoped_client_ids,
        scoped_affiliate_ids: form.scoped_affiliate_ids,
        scoped_campaign_ids: form.scoped_campaign_ids,
      };
      if (mode === "invite") {
        const { data } = await api.post("/employees/invite", { ...base, send_email: form.send_email });
        toast.success("Invitation created");
        try { await navigator.clipboard.writeText(data.invitation_url); }
        catch (err) { devWarn("Clipboard write failed:", err); }
        nav("/employees");
      } else {
        await api.post("/employees/manual", { ...base, temporary_password: form.temporary_password });
        toast.success("Employee created");
        nav("/employees");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="label-mono">Employees</div>
      <h1 className="font-chivo font-black text-3xl uppercase tracking-tighter">
        {mode === "invite" ? "Invite by email" : "Create account manually"}
      </h1>

      <div className="mt-4 flex" data-testid="mode-switch">
        {["invite","manual"].map((m) => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-3 py-2 text-[11px] uppercase tracking-widest border border-black -mr-px ${mode === m ? "bg-black text-white" : "hover:bg-zinc-100"}`}
            data-testid={`mode-${m}`}>
            {m === "invite" ? "Email invitation" : "Manual creation"}
          </button>
        ))}
      </div>

      <form className="brut-card mt-4 p-6 space-y-4" onSubmit={submit}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-mono">Full name*</label>
            <input data-testid="inv-name" required className="w-full border border-black px-3 py-2 mt-1"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Email*</label>
            <input data-testid="inv-email" required type="email" className="w-full border border-black px-3 py-2 mt-1"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>

          {isInternal && (
            <div>
              <label className="label-mono">Organization scope</label>
              <select data-testid="inv-orgtype" className="w-full border border-black px-2 py-1.5 mt-1"
                value={orgType} onChange={(e) => { setOrgType(e.target.value); setForm((f) => ({ ...f, role: "", org_id: "" })); }}>
                <option value="internal">Internal</option>
                <option value="client">Client</option>
                <option value="affiliate">Affiliate</option>
              </select>
            </div>
          )}

          {isInternal && orgType !== "internal" && (
            <div>
              <label className="label-mono">{orgType} org*</label>
              <OrgPicker
                type={orgType}
                value={form.org_id}
                onChange={(id) => setForm((f) => ({ ...f, org_id: id }))}
                required
                testId="inv-org"
                placeholder={`Search ${orgType} orgs by name / email / phone…`}
              />
            </div>
          )}

          <div>
            <label className="label-mono">Role*</label>
            <select data-testid="inv-role" required className="w-full border border-black px-2 py-1.5 mt-1"
              value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="">— select —</option>
              {roleOptions.map((r) => <option key={r} value={r}>{ROLES[r] || r}</option>)}
            </select>
          </div>

          {mode === "manual" && (
            <div>
              <label className="label-mono">Temporary password*</label>
              <input data-testid="inv-temp" required minLength={8} className="w-full border border-black px-3 py-2 mt-1"
                value={form.temporary_password} onChange={(e) => setForm({ ...form, temporary_password: e.target.value })} />
              <div className="text-[11px] text-zinc-500 mt-1">User must change on first login.</div>
            </div>
          )}
        </div>

        <div>
          <div className="label-mono mb-2">Permissions (overrides role defaults)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {(meta.permissions || []).map((p) => (
              <label key={p} className={`border border-black px-2 py-1.5 cursor-pointer flex items-center gap-2 ${form.permissions.includes(p) ? "bg-black text-white" : "hover:bg-zinc-100"}`}>
                <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                <span className="uppercase tracking-widest text-[10px]">
                  {(meta.labels && meta.labels[p]) || p.replaceAll("_", " ").replaceAll(".", " · ")}
                </span>
              </label>
            ))}
          </div>
        </div>

        {mode === "invite" && (
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="inv-sendemail" />
            <span>Send invitation email</span>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-black">
          <button type="button" onClick={() => nav(-1)} className="border border-black px-3 py-2 text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" data-testid="inv-submit" className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
            {mode === "invite" ? "Send invitation" : "Create employee"}
          </button>
        </div>
      </form>
    </div>
  );
}
